// SEO helpers for Maviinci admin panel — per admin-panel-seo-spec.md
// Pure functions only; safe to import anywhere.
import { getCategoryLabel, getAccessoryLabel, labelFor } from '../Data/catalog';
import { homeOf } from '../Data/taxonomy';
import { productSeo } from '../Data/productSeo';

export const SITE_ORIGIN = 'https://maviinci.in';

export const AVAILABILITY_OPTIONS = [
    { value: 'InStock', label: 'In Stock' },
    { value: 'MadeToOrder', label: 'Made to Order' },
    { value: 'PreOrder', label: 'Pre-Order' },
    { value: 'OutOfStock', label: 'Out of Stock' },
];

export const GENDER_OPTIONS = [
    { value: 'male', label: 'Men' },
    { value: 'female', label: 'Women' },
    { value: 'unisex', label: 'Unisex' },
];

export const STATUS_OPTIONS = [
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'archived', label: 'Archived' },
];

export const META_TITLE_MAX = 60;
// Google cuts descriptions at about 155 characters (the website cuts at a full
// sentence within 155 too).
export const META_DESC_MAX = 155;
export const KEYWORDS_MAX = 10;

export const slugify = (raw) => String(raw || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);

export const generateSku = (category) => {
    const cat = String(category || 'PRD').replace(/_/g, '').toUpperCase().slice(0, 6);
    const n = Math.floor(1000 + Math.random() * 9000);
    return `MAV-${cat}-${n}`;
};

const list = (v) => (Array.isArray(v) ? v.filter(Boolean) : []);

// What the product is, in the words customers search with ("kurta set", "bow tie").
const productNoun = ({ kind, category, accessoryType }) => (
    kind === 'accessory'
        ? (getAccessoryLabel(accessoryType) || 'Accessory').split(' / ')[0]
        : getCategoryLabel(category).split(' / ')[0]
).toLowerCase();


// Title and description exactly as the website builds them when these
// fields are left blank (src/Data/productSeo.js, shared with the site), so
// the preview shows what Google will get. Leave the fields blank to keep a
// product on the site's rules as its keywords improve.
const asProduct = (ctx) => ({
    name: ctx.name,
    des: ctx.des,
    material: ctx.material,
    colorName: ctx.colorName,
    category: ctx.category,
    accessoryType: ctx.accessoryType,
    productKind: ctx.kind,
});
export const autoMetaTitle = (ctx) => {
    if (!String(ctx.name || '').trim()) return '';
    const doc = asProduct(ctx);
    return productSeo(doc, homeOf(doc)).title;
};

export const autoMetaDescription = (ctx) => {
    const doc = asProduct(ctx);
    return productSeo(doc, homeOf(doc)).description;
};

export const autoKeywords = (ctx) => {
    const { name, material, pattern, colorName } = ctx;
    const noun = productNoun(ctx);
    const out = new Set();
    if (name) out.add(name.toLowerCase());
    out.add(`${noun} for men`);
    // "buy … online" is what people search; "custom …" has almost no searches
    // in India (docs/seo-keyword-plan.md on the website).
    out.add(`buy ${noun} online`);
    const colour = (colorName || '').toLowerCase() || (list(ctx.colorFamilies)[0] ? labelFor('colors', ctx.colorFamilies[0]).toLowerCase() : '');
    // Only a colour the name gives the garment itself: "Black Formal Suit with
    // White Shirt" is a black suit, not a white one.
    const lowerName = String(name || '').toLowerCase();
    const at = lowerName.indexOf(noun.split(' ').pop());
    const describes = at > 0 ? lowerName.slice(0, at) : lowerName;
    if (colour && describes.includes(colour)) out.add(`${colour} ${noun}`);
    list(ctx.festivals).slice(0, 2).forEach((k) => out.add(`${labelFor('festivals', k).toLowerCase()} ${noun}`));
    list(ctx.occasions).slice(0, 3).forEach((k) => out.add(`${noun} for ${labelFor('occasions', k).toLowerCase()}`));
    list(ctx.roles).slice(0, 2).forEach((k) => out.add(`${labelFor('roles', k).toLowerCase()} outfit`));
    const nounRoot = noun.split(' ').pop().replace(/s$/, '');
    if (material && !material.toLowerCase().includes(nounRoot)) out.add(`${material.toLowerCase()} ${noun}`);
    if (pattern) out.add(`${pattern.toLowerCase()} ${noun}`);
    return Array.from(out).slice(0, KEYWORDS_MAX);
};

export const autoCanonicalUrl = ({ category, slug, docId }) => {
    if (!category || !slug || !docId) return '';
    return `${SITE_ORIGIN}/products/${category}/${slug}/${docId}`;
};

export const autoAltText = ({ colorName, name, view }) => {
    const parts = [];
    const nameLc = String(name || '').toLowerCase();
    const colourWords = String(colorName || '').toLowerCase().split(/[^a-z]+/).filter((w) => w && w !== 'and');
    if (colorName && !colourWords.every((w) => nameLc.includes(w))) parts.push(colorName);
    if (name) parts.push(name);
    const base = parts.join(' ').trim();
    if (!base) return '';
    return view ? `${base} – ${view}` : base;
};

export const countCharsClass = (value, max) => {
    const len = (value || '').length;
    if (!len) return 'empty';
    if (len > max) return 'over';
    if (len > max - 10) return 'warn';
    return 'ok';
};
